import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CrossIcon } from '../../../common/icons';
import { dismissDegreesWarning } from '../actions/arSession';

/**
 * Warns that the sketch uses `angleMode(DEGREES)`, which the AR bridge cannot honour.
 *
 * Why this exists: degrees are perfectly valid p5 and render correctly in the editor
 * preview. The AR bridge forces radians every frame, and the converter strips
 * `angleMode(DEGREES)` rather than fight it — so the angles are wrong on the PHONE ONLY.
 * The two realms disagree with no error anywhere, which is the hardest kind of bug to
 * find here. This bar is the error.
 *
 * Shown only while an AR session is live: the flag is set by the AR publish path, so a
 * student working purely in the preview is never nagged about valid p5.
 *
 * Dismissal deliberately survives repeated Runs (a student may press Run many times while
 * still holding degrees) but re-arms if the sketch stops and then starts using degrees
 * again. Both rules live in the arSession reducer.
 */
export default function DegreesWarningBar() {
  const { usesDegrees, degreesDismissed } = useSelector(
    (state) => state.arSession
  );
  const dispatch = useDispatch();
  const { t } = useTranslation();

  if (!usesDegrees || degreesDismissed) return null;

  return (
    <div className="degrees-warning" role="alert">
      <p className="degrees-warning__message">
        {t('ARDegreesWarning.Message')}{' '}
        <a
          className="degrees-warning__link"
          href="/ar-reference/limits"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('ARDegreesWarning.LearnMore')}
        </a>
      </p>
      <button
        className="degrees-warning__close"
        onClick={() => dispatch(dismissDegreesWarning())}
        aria-label={t('ARDegreesWarning.DismissARIA')}
      >
        <CrossIcon Icon={{ default: '#fff', hover: '#f4d5d5' }} />
      </button>
    </div>
  );
}
